(() => {
  const slugify = (value) => value
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '') || 'section';

  const unwrapLegacyMarkup = (content) => {
    const nestedBody = content.querySelector('.kb-body');
    if (nestedBody && nestedBody !== content) {
      const legacyWrapper = nestedBody.parentElement;
      if (legacyWrapper && content.contains(legacyWrapper)) {
        while (nestedBody.firstChild) {
          content.insertBefore(nestedBody.firstChild, legacyWrapper);
        }
        legacyWrapper.remove();
      }
    }
    content.querySelectorAll('.kb-toc, style, script').forEach((node) => node.remove());
  };

  const ensureTableWrappers = (content) => {
    content.querySelectorAll('table').forEach((table) => {
      if (table.closest('.kb-table')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'kb-table';
      wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', table.getAttribute('aria-label') || 'Article table');
      wrapper.tabIndex = 0;
      table.parentNode.insertBefore(wrapper, table);
      wrapper.append(table);
    });
  };

  const ensureLightbox = () => {
    let dialog = document.querySelector('.kb-editorial-lightbox');
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'kb-editorial-lightbox';
    dialog.setAttribute('aria-label', 'Enlarged article image');
    const close = document.createElement('button');
    close.type = 'button';
    close.textContent = '×';
    close.setAttribute('aria-label', 'Close enlarged image');
    const large = document.createElement('img');
    dialog.append(close, large);
    document.body.append(dialog);

    let origin;
    let overflow;
    dialog.kbOpen = (img) => {
      origin = img;
      overflow = document.body.style.overflow;
      let source = img.currentSrc || img.src;
      try {
        const url = new URL(source, location.href);
        if (url.pathname.includes('/cdn/shop/')) {
          url.searchParams.set('width', '2000');
          source = url.href;
        }
      } catch (_err) {}
      large.src = source;
      large.alt = img.alt || '';
      document.body.style.overflow = 'hidden';
      dialog.showModal();
      close.focus();
    };
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      document.body.style.overflow = overflow || '';
      origin?.focus({ preventScroll: true });
    });
    return dialog;
  };

  const setupZoom = (root) => {
    const dialog = ensureLightbox();
    root.querySelectorAll('.kiwibit-blog-editorial__hero img, .kiwibit-blog-editorial__content img').forEach((img) => {
      if (img.closest('.kb-product, .kb-collection, a') || img.dataset.kbZoom) return;
      img.dataset.kbZoom = 'true';
      img.classList.add('kb-zoomable');
      img.tabIndex = 0;
      img.setAttribute('role', 'button');
      img.setAttribute('aria-label', `Enlarge image: ${img.alt || 'article image'}`);
      img.addEventListener('click', () => dialog.kbOpen(img));
      img.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          dialog.kbOpen(img);
        }
      });
    });
  };

  const buildTocAndTracking = (root) => {
    if (root.dataset.editorialReady === 'true') return;
    const content = root.querySelector('.kiwibit-blog-editorial__content');
    const toc = root.querySelector('[data-blog-toc]');
    const list = root.querySelector('[data-blog-toc-list]');
    if (!content || !toc || !list) return;

    unwrapLegacyMarkup(content);
    ensureTableWrappers(content);

    const headings = [...content.querySelectorAll('h2, h3')].filter(
      (heading) => !heading.closest('.kb-product, .kb-collection')
    );
    const used = new Map();
    const items = [];
    list.innerHTML = '';

    headings.forEach((heading) => {
      const label = heading.textContent.trim();
      if (!label) return;
      const base = slugify(label);
      const count = (used.get(base) || 0) + 1;
      used.set(base, count);
      heading.id = heading.id || (count === 1 ? base : `${base}-${count}`);
      const level = heading.tagName.slice(1);
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = label;
      link.dataset.level = level;
      link.className = `kb-level-${level}`;
      list.append(link);
      items.push({ link, heading });
    });

    if (!items.length) {
      toc.setAttribute('hidden', '');
    } else {
      toc.removeAttribute('hidden');
      let active = null;
      let pending = false;
      let hold = 0;

      const update = () => {
        pending = false;
        let selected = items[0];
        for (const item of items) {
          if (item.heading.getBoundingClientRect().top <= 172) selected = item;
          else break;
        }
        if (active === selected.link) return;
        active = selected.link;
        for (const item of items) {
          const isCurrent = item.link === active;
          item.link.classList.toggle('kb-current', isCurrent);
          if (isCurrent) item.link.setAttribute('aria-current', 'location');
          else item.link.removeAttribute('aria-current');
        }
        if ((toc.open === undefined || toc.open) && Date.now() > hold) {
          const navRect = list.getBoundingClientRect();
          const activeRect = active.getBoundingClientRect();
          if (activeRect.top < navRect.top + 12) {
            list.scrollTop += activeRect.top - navRect.top - 12;
          } else if (activeRect.bottom > navRect.bottom - 12) {
            list.scrollTop += activeRect.bottom - navRect.bottom + 12;
          }
        }
      };

      const schedule = () => {
        if (!pending) {
          pending = true;
          requestAnimationFrame(update);
        }
      };

      list.addEventListener('wheel', () => { hold = Date.now() + 1500; }, { passive: true });
      list.addEventListener('touchmove', () => { hold = Date.now() + 1500; }, { passive: true });
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      window.addEventListener('load', schedule, { once: true });
      update();
    }

    setupZoom(root);
    root.dataset.editorialReady = 'true';
  };

  const init = () => document.querySelectorAll('[data-kiwibit-blog-editorial]').forEach(buildTocAndTracking);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
